import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  Check,
  Mic,
  Waveform,
  FileText,
  PenTool,
  ShieldCheck,
  Lock,
  Database,
  Server,
  ScrollText,
  AudioLines,
  Zap,
  Layers,
  Languages,
  CircleSlash,
  Clock,
  Sparkles,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Phone,
  Mail,
  MessageCircle,
  IndianRupee,
  Smartphone,
  Monitor,
  Globe,
  FilePenLine,
  Pencil,
  UserCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  RADSCRIBE_WA_LINK,
  CONTACT_PHONE,
  // CONTACT_EMAIL,
} from "@/lib/constants";

export const metadata = {
  title:
    "RadScribe | AI Voice Dictation & Reporting Assistant for Radiologists",

  description:
    "RadScribe turns a radiologist's spoken findings into a complete structured, review-ready report, in the practice's own template, correct terminology, normals intact, ready to review and sign. It replaces the typist.",

  keywords: [
    "Radiology dictation software",
    "Voice AI for radiologists",
    "Medical dictation AI",
    "Radiology speech recognition",
    "AI medical transcription",
    "Radiology reporting assistant",
    "Voice to report software",
    "Clinical dictation software",
    "Healthcare AI documentation",
    "Radiology workflow automation",
  ],

  alternates: {
    canonical: "https://zeltaa.com/radscribe",
  },

  openGraph: {
    title:
      "RadScribe | AI Voice Dictation & Reporting Assistant for Radiologists",
    description:
      "RadScribe turns a radiologist's spoken findings into a complete report, in the practice's own template, correct terminology, normals intact, ready to review and sign. It replaces the typist.",
    url: "https://zeltaa.com/radscribe",
    image: "/radscribe.png",
  },
};

const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "RadScribe",
    applicationCategory: "HealthcareApplication",
    operatingSystem: "Cloud",
    description:
      "RadScribe turns a radiologist's spoken findings into a complete report, in the practice's own template, correct terminology, normals intact, ready to review and sign. It replaces the typist.",
    url: "https://zeltaa.com/radscribe",
    publisher: {
      "@type": "Organization",
      name: "Zeltaa",
      url: "https://zeltaa.com",
    },
  };
  
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

// ============ HERO ============
function Hero() {
  return (
    <section
      id="top"
      data-testid="rs-hero-section"
      className="relative isolate overflow-hidden bg-[#071B34] pt-32 pb-20 lg:pb-28"
    >
      <div className="hero-glow" aria-hidden="true" />
      <div className="intelligence-grid" aria-hidden="true" />
      <div className="noise-overlay" aria-hidden="true" />
      <div className="absolute top-1/3 -right-32 w-[460px] h-[460px] rounded-full bg-[#14D6C7]/10 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-20 left-0 w-[520px] h-[520px] rounded-full bg-[#2EE8FF]/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#14D6C7]/25 bg-[#14D6C7]/5 backdrop-blur-sm mb-7"
            >
              <span className="pulse-dot" />
              <span className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#14D6C7] font-manrope">
                Voice → Report · India-first
              </span>
            </motion.div>

            <motion.h1
              data-testid="rs-hero-headline"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              className="text-balance font-manrope font-bold tracking-[-0.025em] text-white text-[clamp(2.2rem,5.6vw,4.6rem)] leading-[1.04]"
            >
              Rough dictation in.{" "}
              <span className="bg-gradient-to-r from-[#14D6C7] via-[#2EE8FF] to-[#14D6C7] bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer">
                Finished report out.
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-7 max-w-xl font-poppins text-base sm:text-lg text-white/65 leading-relaxed"
            >
              RadScribe turns a radiologist&apos;s spoken findings into a complete report —
              in the practice&apos;s own template, correct terminology, normals intact,
              ready to review and sign. It replaces the typist.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                data-testid="rs-hero-demo-btn"
                href={RADSCRIBE_WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shine group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope"
              >
                Book a Demo
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
              </a>
              <a
                data-testid="rs-hero-how-btn"
                href="#how"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-semibold hover:bg-white/[0.08] hover:border-[#14D6C7]/40 transition-all duration-300 font-manrope backdrop-blur-sm"
              >
                See How It Works
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2.2} />
              </a>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/55 font-poppins"
            >
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> Template-fill, not free-write
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> Zero fabrication
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> You review & sign
              </span>
            </motion.div>
          </div>

          {/* Dictation visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute inset-x-6 -top-6 h-10 bg-[#14D6C7]/30 blur-2xl rounded-full" aria-hidden="true" />
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-md overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#14D6C7]/15 rounded-full blur-3xl" />

              {/* mic capture row */}
              <div className="relative px-7 pt-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#14D6C7]/15 border border-[#14D6C7]/40">
                    <Mic className="w-4 h-4 text-[#14D6C7]" strokeWidth={2} />
                    <span className="absolute -inset-1 rounded-full border border-[#14D6C7]/30 animate-ping" />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-bold font-manrope">
                      Dictating · LIVE
                    </div>
                    <div className="text-xs text-white/55 font-poppins">MRI · Lumbar Spine</div>
                  </div>
                  <span className="ml-auto text-[10px] font-mono text-white/40">00:14</span>
                </div>

                {/* waveform */}
                <div className="flex items-end gap-[3px] h-12 mb-5" aria-hidden="true">
                  {Array.from({ length: 56 }).map((_, i) => {
                    const h = 20 + Math.abs(Math.sin(i * 0.7) * 30) + (i % 5) * 4;
                    return (
                      <span
                        key={i}
                        className="flex-1 rounded-full bg-gradient-to-t from-[#14D6C7]/60 to-[#2EE8FF]"
                        style={{
                          height: `${Math.min(h, 48)}px`,
                          opacity: 0.4 + (i / 56) * 0.6,
                          animation: `pulse-glow ${1.4 + (i % 7) * 0.15}s ease-in-out infinite`,
                        }}
                      />
                    );
                  })}
                </div>

                {/* spoken */}
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-manrope font-semibold mb-2">
                    What you say
                  </div>
                  <p className="text-sm text-white/90 font-poppins italic leading-relaxed">
                    &ldquo;L4–L5 disc bulge, mild canal stenosis, rest unremarkable.&rdquo;
                  </p>
                </div>
              </div>

              {/* arrow */}
              <div className="relative flex items-center justify-center my-4">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#14D6C7] text-[#071B34]">
                  <ArrowDown className="w-4 h-4" strokeWidth={2.4} />
                </span>
              </div>

              {/* signed */}
              <div className="relative px-7 pb-7">
                <div className="rounded-lg border border-[#14D6C7]/30 bg-[#14D6C7]/[0.05] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold">
                      What you sign
                    </div>
                    <FileText className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={1.6} />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-manrope font-bold mb-2">
                    MRI — Lumbar Spine
                  </div>
                  <p className="text-[13px] text-white/85 font-poppins leading-relaxed">
                    Diffuse posterior disc bulge at <span className="text-[#14D6C7]">L4–L5</span> indenting the thecal sac,
                    with <span className="text-[#14D6C7]">mild central canal stenosis</span>.
                    Vertebral alignment, remaining discs and the visualised cord are unremarkable.
                  </p>
                  <p className="mt-2 text-[12px] text-white/65 font-poppins">
                    <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">Impression: </span>
                    L4–L5 disc bulge with mild canal stenosis.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick metrics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] backdrop-blur-md"
        >
          {[
            { v: "Seconds", l: "Not minutes" },
            { v: "0%", l: "Fabrication" },
            { v: "24/7", l: "Always on" },
            { v: "Your", l: "Template, verbatim" },
          ].map((m, i) => (
            <div key={m.l} className="relative px-6 py-7 bg-[#071B34]/60 text-left">
              <div className="text-[#14D6C7] text-2xl sm:text-3xl font-bold font-manrope tracking-tight">{m.v}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/45 font-poppins">{m.l}</div>
              {i < 3 && (
                <span className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px bg-white/10" />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============ PROBLEM ============
function Problem() {
  return (
    <section
      data-testid="rs-problem-section"
      id="problem"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            01 — The Problem
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            A radiologist&apos;s scarcest resource is{" "}
            <span className="text-[#14D6C7]">wasted on a typist.</span>
          </h2>
        </motion.div>

        {/* India context */}
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { v: "1 : 100,000", l: "Radiologists per person in India" },
            { v: "10–15K", l: "Radiologists for 1.4B people" },
            { v: "3–4% / yr", l: "Imaging volume growth" },
          ].map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="p-8 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/30 transition-colors"
            >
              <div className="text-4xl sm:text-5xl font-bold font-manrope text-[#071B34] tracking-tight">{s.v}</div>
              <div className="mt-2 text-sm text-[#6C7A89] font-poppins">{s.l}</div>
            </motion.div>
          ))}
        </div>

        {/* Three painful realities */}
        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {[
            {
              tag: "Errors",
              icon: AlertTriangle,
              copy: "Non-medical typists mishear and misspell terms — every report needs a slow correction round-trip.",
            },
            {
              tag: "Waste",
              icon: CircleSlash,
              copy: "To compensate, radiologists over-articulate every negative — burning the system's scarcest time.",
            },
            {
              tag: "Cost",
              icon: IndianRupee,
              copy: "A typist costs ₹15,000–30,000/mo, works fixed hours, can't scale, and still makes errors.",
            },
          ].map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="p-7 rounded-2xl border border-[#6C7A89]/15 bg-white"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#F9FBFD] border border-[#6C7A89]/15 text-[#071B34]">
                    <Icon className="w-4 h-4" strokeWidth={1.7} />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-manrope font-bold text-[#071B34]">
                    {card.tag}
                  </span>
                </div>
                <p className="text-[15px] text-[#2B3440] font-poppins leading-relaxed">{card.copy}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============ TRANSFORMATION ============
function Transformation() {
  return (
    <section
      data-testid="rs-transformation-section"
      id="example"
      className="relative bg-[#071B34] py-24 sm:py-32 overflow-hidden"
    >
      <div className="intelligence-grid opacity-40" aria-hidden="true" />
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#14D6C7]/10 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            02 — The transformation
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
            Speak naturally.{" "}
            <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
              Get a finished report in your template.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 items-stretch">
          {/* SAY */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-8 rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-sm"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="inline-flex items-center gap-2">
                <AudioLines className="w-4 h-4 text-[#14D6C7]" strokeWidth={1.6} />
                <span className="text-xs uppercase tracking-[0.22em] font-manrope font-bold text-[#14D6C7]">
                  What you say
                </span>
              </div>
              <span className="text-[10px] font-mono text-white/40">00:14 · Live mic</span>
            </div>

            <div className="flex items-end gap-[3px] h-10 mb-6" aria-hidden="true">
              {Array.from({ length: 48 }).map((_, i) => {
                const h = 14 + Math.abs(Math.sin(i * 0.9 + 1) * 24) + (i % 4) * 4;
                return (
                  <span
                    key={i}
                    className="flex-1 rounded-full bg-gradient-to-t from-[#14D6C7]/50 to-[#2EE8FF]"
                    style={{ height: `${Math.min(h, 40)}px`, opacity: 0.4 + (i / 48) * 0.6 }}
                  />
                );
              })}
            </div>

            <blockquote className="text-xl sm:text-2xl font-poppins italic text-white/90 leading-relaxed">
              &ldquo;L4–L5 disc bulge, mild canal stenosis, rest unremarkable.&rdquo;
            </blockquote>
            <p className="mt-4 text-xs text-white/45 font-poppins">
              Findings in fragments and shorthand. No dictating every negative, no spelling out terms.
            </p>
          </motion.div>

          {/* SIGN */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-8 rounded-2xl border border-[#14D6C7]/40 bg-gradient-to-br from-[#14D6C7]/10 to-transparent backdrop-blur-sm"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#14D6C7]/20 blur-3xl rounded-full" />
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="inline-flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#14D6C7]" strokeWidth={1.6} />
                  <span className="text-xs uppercase tracking-[0.22em] font-manrope font-bold text-[#14D6C7]">
                    What you sign
                  </span>
                </div>
                <span className="text-[10px] font-mono text-white/40">Draft · ready</span>
              </div>

              <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 font-manrope font-bold mb-3">
                MRI — Lumbar Spine
              </div>
              <p className="text-[15px] text-white/90 font-poppins leading-relaxed">
                Diffuse posterior disc bulge at <span className="text-[#14D6C7] font-medium">L4–L5</span> indenting the thecal sac,
                with <span className="text-[#14D6C7] font-medium">mild central canal stenosis</span>.
                Vertebral alignment, remaining discs and the visualised cord are unremarkable.
              </p>
              <p className="mt-4 text-sm text-white/75 font-poppins">
                <span className="text-white/45 uppercase tracking-wider text-[10px] font-bold">Impression: </span>
                L4–L5 disc bulge with mild canal stenosis.
              </p>

              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs text-white/45 font-poppins italic">
                  Your phrasing, normals & sign-off — verbatim.
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#14D6C7] font-manrope font-semibold">
                  <UserCheck className="w-3.5 h-3.5" /> Review &amp; sign
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 text-center text-base sm:text-lg font-manrope text-white/70 max-w-3xl mx-auto"
        >
          Your words. Your template. Your signature.{" "}
          <span className="text-white font-semibold">RadScribe just writes it down — correctly.</span>
        </motion.p>
      </div>
    </section>
  );
}

// ============ HOW IT WORKS ============
function HowItWorks() {
  const steps = [
    {
      n: "01",
      tag: "Dictate",
      icon: Mic,
      title: "Speak it like a colleague",
      desc:
        "Findings in fragments and shorthand — \"rest normal.\" No dictating every negative, no spelling out terms.",
    },
    {
      n: "02",
      tag: "Fill the template",
      icon: FilePenLine,
      title: "It writes your report",
      desc:
        "RadScribe routes to your house format and rewrites only the abnormal lines, keeping your phrasing, normals and sign-off verbatim.",
    },
    {
      n: "03",
      tag: "Review & sign",
      icon: PenTool,
      title: "You stay in control",
      desc:
        "A draft on your exact letterhead — changes highlighted, anything ambiguous flagged. You review, edit if needed, and sign.",
    },
  ];

  return (
    <section
      id="how"
      data-testid="rs-how-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            03 — How it works
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            Three steps. The radiologist{" "}
            <span className="text-[#14D6C7]">stays the author.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative p-8 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/40 hover:shadow-[0_24px_50px_-22px_rgba(20,214,199,0.28)] transition-all duration-400 group"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-3xl font-bold font-manrope text-[#14D6C7]/30 group-hover:text-[#14D6C7] transition-colors">
                    {s.n}
                  </span>
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/10 border border-[#14D6C7]/20 text-[#14D6C7]">
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
                  </span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-2">
                  {s.tag}
                </div>
                <h3 className="text-xl font-manrope font-bold text-[#071B34] leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-[#6C7A89] font-poppins leading-relaxed">
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============ BENEFITS ============
function Benefits() {
  const items = [
    {
      icon: Zap,
      tag: "Speed",
      title: "Seconds, not minutes",
      desc: "A finished draft the moment you stop talking — no typist queue, no rework.",
    },
    {
      icon: Layers,
      tag: "Your format",
      title: "Reports in your house template",
      desc: "Normals, phrasing and sign-off stay verbatim; only the abnormal lines change.",
    },
    {
      icon: Languages,
      tag: "Accuracy",
      title: "Correct radiology language",
      desc: "Terminology, laterality and measurements right the first time.",
    },
    {
      icon: CircleSlash,
      tag: "Less effort",
      title: "Stop narrating every normal",
      desc: "Speak only what matters — the template fills in every normal for you.",
    },
    {
      icon: Clock,
      tag: "Always on",
      title: "No fixed hours, scales with you",
      desc: "Around the clock, at any volume, for a fraction of a typist's cost.",
    },
  ];

  return (
    <section
      id="benefits"
      data-testid="rs-benefits-section"
      className="relative bg-[#071B34] py-24 sm:py-32 overflow-hidden"
    >
      <div className="intelligence-grid opacity-40" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-[#2EE8FF]/8 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            04 — Why RadScribe
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
            It does the typing.{" "}
            <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
              You do the medicine.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => {
            const Icon = it.icon;
            const featured = i === 0;
            return (
              <motion.div
                key={it.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className={`p-7 rounded-2xl border backdrop-blur-sm transition-all duration-400 ${
                  featured
                    ? "border-[#14D6C7]/40 bg-gradient-to-br from-[#14D6C7]/10 to-transparent lg:row-span-2"
                    : "border-white/10 bg-white/[0.02] hover:border-[#14D6C7]/30"
                }`}
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/15 border border-[#14D6C7]/30 text-[#14D6C7] mb-5">
                  <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
                </span>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-2">
                  {it.tag}
                </div>
                <h3 className={`font-manrope font-bold text-white leading-snug ${featured ? "text-2xl" : "text-lg"}`}>
                  {it.title}
                </h3>
                <p className={`mt-3 font-poppins text-white/55 leading-relaxed ${featured ? "text-base" : "text-sm"}`}>
                  {it.desc}
                </p>
                {featured && (
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-2 text-xs text-white/70 font-poppins">
                    <Sparkles className="w-3.5 h-3.5 text-[#14D6C7]" />
                    A finished draft, the moment you stop talking.
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============ ARCHITECTURE ============
function Architecture() {
  const blocks = [
    {
      icon: Mic,
      tag: "Voice capture",
      desc: "Phone-as-mic, web and desktop.",
      pills: [
        { icon: Smartphone, label: "Phone" },
        { icon: Globe, label: "Web" },
        { icon: Monitor, label: "Desktop" },
      ],
    },
    {
      icon: Layers,
      tag: "Template engine",
      desc:
        "Per-radiologist and per-centre templates, macros, normal-study defaults — modality- and body-part-aware.",
    },
    {
      icon: Languages,
      tag: "Radiology LLM",
      desc:
        "Terminology, laterality, measurements, standard phrasing; optional consensus follow-ups (Fleischner, thyroid).",
    },
    {
      icon: UserCheck,
      tag: "Human-in-loop",
      desc: "Always an unsigned draft; the radiologist is author and signatory.",
    },
    {
      icon: FileText,
      tag: "Integrations",
      desc: "PDF / .docx letterhead first.",
    },
  ];

  return (
    <section
      id="architecture"
      data-testid="rs-architecture-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            05 — Architecture
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            Built around the{" "}
            <span className="text-[#14D6C7]">solo-centre reality.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="p-7 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/40 hover:shadow-[0_20px_40px_-20px_rgba(20,214,199,0.22)] transition-all"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/10 border border-[#14D6C7]/20 text-[#14D6C7] mb-5">
                  <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
                </span>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-2">
                  {b.tag}
                </div>
                <p className="text-[15px] text-[#2B3440] font-poppins leading-relaxed">
                  {b.desc}
                </p>
                {b.pills && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {b.pills.map((p) => {
                      const PIcon = p.icon;
                      return (
                        <span
                          key={p.label}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9FBFD] border border-[#6C7A89]/15 text-xs text-[#2B3440] font-medium font-manrope"
                        >
                          <PIcon className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={1.8} />
                          {p.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============ TRUST & SAFETY ============
function TrustSafety() {
  const pillars = [
    {
      tag: "Template-fill",
      title: "Never free-write",
      icon: FilePenLine,
      desc:
        "Start from the practice's normal template; rewrite only abnormal lines; keep approved normals, phrasing and sign-off verbatim.",
    },
    {
      tag: "Zero fabrication",
      title: "Traced and verified, every time",
      icon: ShieldCheck,
      desc:
        "Every line traces to the dictation or the template. A faithfulness check runs each time — any unsupported clinical content blocks release.",
    },
    {
      tag: "Human-in-loop",
      title: "The radiologist is the author",
      icon: UserCheck,
      desc:
        "Output is always an unsigned draft for review and signature. A drafting tool with sign-off — never an autonomous device.",
    },
  ];

  const compliance = [
    { icon: ShieldCheck, label: "DPDP Act 2023" },
    { icon: Database, label: "India data residency" },
    { icon: Lock, label: "PHI never sent to STT / LLM vendors" },
    { icon: ScrollText, label: "Immutable audit log" },
    { icon: Server, label: "No-train vendor terms" },
  ];

  return (
    <section
      id="trust"
      data-testid="rs-trust-section"
      className="relative bg-[#071B34] py-24 sm:py-32 overflow-hidden"
    >
      <div className="intelligence-grid opacity-50" aria-hidden="true" />
      <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] bg-[#14D6C7]/10 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            06 — Trust & safety
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
            Zero fabrication is a{" "}
            <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
              hard release gate.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="p-7 rounded-2xl border border-[#14D6C7]/20 bg-white/[0.02] backdrop-blur-sm"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/15 border border-[#14D6C7]/30 text-[#14D6C7]">
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-2">
                  {p.tag}
                </div>
                <h3 className="text-lg font-manrope font-bold text-white leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-white/60 font-poppins leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Compliance strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm p-7"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> India-first compliance
            </div>
            <ul className="flex flex-wrap gap-x-7 gap-y-3">
              {compliance.map((c) => {
                const Icon = c.icon;
                return (
                  <li key={c.label} className="inline-flex items-center gap-2 text-sm text-white/75 font-poppins">
                    <Icon className="w-4 h-4 text-[#14D6C7]" strokeWidth={1.6} />
                    {c.label}
                  </li>
                );
              })}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ FINAL CTA ============
function FinalCTA() {
  return (
    <section
      id="cta"
      data-testid="rs-cta-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-3xl bg-[#071B34] p-10 sm:p-16 lg:p-20"
        >
          <div className="intelligence-grid opacity-60" aria-hidden="true" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#14D6C7]/15 blur-[120px]" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#2EE8FF]/10 blur-[100px]" />

          <div className="relative grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
                <span className="block w-8 h-px bg-[#14D6C7]" />
                Ready to replace the typist?
              </div>
              <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
                Speak it.{" "}
                <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
                  Sign it.
                </span>
              </h2>
              <p className="mt-5 text-base sm:text-lg font-poppins text-white/60 max-w-xl leading-relaxed">
                RadScribe turns rough dictation into a finished report in your own
                template — accurate, faithful to what you said, and always yours to
                review and sign.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  data-testid="rs-cta-demo-btn"
                  href={RADSCRIBE_WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-shine inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
                  Book a Demo on WhatsApp
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
                </a>
                <a
                  data-testid="rs-cta-call-btn"
                  href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-semibold hover:bg-white/[0.08] hover:border-[#14D6C7]/40 transition-all duration-300 font-manrope backdrop-blur-sm"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.2} />
                  {CONTACT_PHONE}
                </a>
              </div>

              {/* <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/50 font-poppins">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1.5 link-underline">
                  <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
                </a>
              </div> */}
            </div>

            {/* Pillars card */}
            <div className="lg:col-span-5">
              <div className="relative p-8 rounded-2xl border border-[#14D6C7]/30 bg-white/[0.03] backdrop-blur-md">
                <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-5">
                  The release contract
                </div>
                <ul className="space-y-4">
                  {[
                    { title: "Template-fill, not free-write", icon: FilePenLine },
                    { title: "Zero fabrication", icon: ShieldCheck },
                    { title: "You review & sign", icon: UserCheck },
                  ].map((p) => {
                    const Icon = p.icon;
                    return (
                      <li key={p.title} className="flex items-center gap-3">
                        <span className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#14D6C7]/15 border border-[#14D6C7]/30 text-[#14D6C7]">
                          <Icon className="w-4 h-4" strokeWidth={1.7} />
                        </span>
                        <span className="text-base font-manrope font-semibold text-white">
                          {p.title}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="my-6 h-px bg-white/10" />
                <p className="text-xs text-white/45 font-poppins italic leading-relaxed">
                  Voice to report. Intelligence to impact.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ PAGE ============
export default function RadScribe() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "RadScribe — Voice-to-Report Intelligence | Zeltaa";
  }, []);

  return (
    <main data-testid="radscribe-landing">
      <Navbar />
      <Hero />
      <Problem />
      <Transformation />
      <HowItWorks />
      <Benefits />
      <Architecture />
      <TrustSafety />
      <FinalCTA />
      <Footer />
    </main>
  );
}
